// SocketContext.tsx
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { API_BASE_URL } from '../constants/config';
import { io, Socket } from 'socket.io-client';
import { useUserStore } from '../shared/stores/useUserStore';
import { getAndSanitize } from '../shared/utils/sanitization';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting' | 'error';
  lastPing: number | null;
  emit: (event: string, data?: any) => void;
  lastReconnectTimestamp: number;
  health: {
    status: 'ok' | 'error';
    ping: number;
    timestamp: number;
    memory: any;
    cpu: any;
  };
  updatePanelId: (panelId: string) => void;
  grantUserAccess: (userId: string, username: string) => void;
  // 🚀 Nuevos métodos para eventos de tickets
  onTicketCreated: (callback: (data: any) => void) => void;
  onTicketStatusChanged: (callback: (data: any) => void) => void;
  onNewTicketMessage: (callback: (data: any) => void) => void;
  offTicketCreated: (callback: (data: any) => void) => void;
  offTicketStatusChanged: (callback: (data: any) => void) => void;
  offNewTicketMessage: (callback: (data: any) => void) => void;
  // 🔔 Nuevos métodos para notificaciones
  onUserInfoNotification: (callback: (data: any) => void) => void;
  offUserInfoNotification: (callback: (data: any) => void) => void;
  onUserInfoNotificationResolved: (callback: (data: any) => void) => void;
  offUserInfoNotificationResolved: (callback: (data: any) => void) => void;
}

const defaultHealth = {
  status: 'error' as const,
  ping: 0,
  timestamp: Date.now(),
  memory: {},
  cpu: {}
};

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  connectionStatus: 'disconnected',
  lastPing: null,
  emit: () => {},
  lastReconnectTimestamp: 0,
  health: defaultHealth,
  updatePanelId: () => {},
  grantUserAccess: () => {},
  onTicketCreated: () => {},
  onTicketStatusChanged: () => {},
  onNewTicketMessage: () => {},
  offTicketCreated: () => {},
  offTicketStatusChanged: () => {},
  offNewTicketMessage: () => {},
  onUserInfoNotification: () => {},
  offUserInfoNotification: () => {},
  onUserInfoNotificationResolved: () => {},
  offUserInfoNotificationResolved: () => {}
});

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting' | 'error'>('disconnected');
  const [lastPing, setLastPing] = useState<number | null>(null);
  const [lastReconnectTimestamp, setLastReconnectTimestamp] = useState(0);
  const [health, setHealth] = useState(defaultHealth);
  const [currentPanelId, setCurrentPanelId] = useState<string | null>(null);
  const clientId = useRef<string>(`client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`);
  const pendingMessages = useRef<Array<{event: string, data: any}>>([]);

  // Initialize socket connection
  useEffect(() => {
    const token = getAndSanitize('token');
    if (!token) return;

    setConnectionStatus('connecting');
    
    // Create socket connection
    const newSocket = io(API_BASE_URL, {
      path: '/socket.io',
      auth: {
        token,
        clientId: clientId.current
      },
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      transports: ['websocket', 'polling']
    });

    // Setup basic event listeners
    newSocket.on('connect', () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      setLastReconnectTimestamp(Date.now());
      
      // Send registration message
      const adminId = extractUserId(token);
      newSocket.emit('register', {
        client_time: Date.now(),
        client_id: clientId.current,
        admin_id: adminId,
        panel_id: currentPanelId,
        client_info: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        }
      });
      
      // Register panel if available
      if (currentPanelId) {
        newSocket.emit('registerPanel', {
          adminId,
          panelId: currentPanelId
        });
      }
      
      // Send pending messages
      if (pendingMessages.current.length > 0) {
        pendingMessages.current.forEach(msg => {
          newSocket.emit(msg.event, msg.data);
        });
        pendingMessages.current = [];
      }
      
      // Get active panelId for user
      fetchActivePanelId(newSocket, token);
      
      // Request initial state
      newSocket.emit('getPanelStatus');
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });
    
    newSocket.on('connect_error', (err) => {
      console.error('[Socket] Connection error:', err);
      setConnectionStatus('error');
    });
    
    // Other event handlers
    newSocket.on('userRequestAccess', () => {
    });

    newSocket.on('accessGranted', () => {
    });

    newSocket.on('health_check', (data) => {
      const ping = Date.now() - data.timestamp;
      
      // Avoid unnecessary updates if changes are minimal
      setHealth(prevHealth => {
        // Only update if there are significant changes
        const significantPingChange = Math.abs(prevHealth.ping - ping) > 50; // Difference greater than 50ms
        const statusChange = prevHealth.status !== (ping < 1000 ? 'ok' : 'error');
        
        // Check for significant memory changes
        const prevMemoryUsed = (prevHealth.memory as any)?.heapUsed || 0;
        const newMemoryUsed = (data.memory as any)?.heapUsed || 0;
        const significantMemoryChange = Math.abs(prevMemoryUsed - newMemoryUsed) > 10000000; // 10MB
        
        // Check for significant CPU changes
        const prevCpuUsage = (prevHealth.cpu as any)?.user || 0;
        const newCpuUsage = (data.cpu as any)?.user || 0;
        const significantCpuChange = Math.abs(prevCpuUsage - newCpuUsage) > 50000000; // Significant threshold
        
        // Only update if there are important changes
        if (significantPingChange || statusChange || significantMemoryChange || significantCpuChange) {
          return {
            ...data,
            ping,
            status: ping < 1000 ? 'ok' : 'error'
          };
        }
        
        // If no significant changes, keep previous state
        return prevHealth;
      });
    });
    
    newSocket.on('ping', (data) => {
      newSocket.emit('pong', { timestamp: data.time });
    });
    
    newSocket.on('pong', () => {
      setLastPing(Date.now());
    });

    newSocket.on('welcome', () => {
    });

    // 🚀 Eventos de tickets
    newSocket.on('ticket_created', () => {
      // Los callbacks se manejarán a través de los métodos específicos
    });

    newSocket.on('ticket_status_changed', () => {
      // Los callbacks se manejarán a través de los métodos específicos
    });

    newSocket.on('new_ticket_message', () => {
      // Los callbacks se manejarán a través de los métodos específicos
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      if (newSocket) {
        newSocket.removeAllListeners();
        newSocket.disconnect();
      }
    };
  }, []);

  // Extract userId from JWT token
  const extractUserId = (token: string): string | null => {
    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      return tokenData.userId || tokenData.id || tokenData.sub;
    } catch (err) {
      console.error('[Socket] Failed to extract userId from token:', err);
      return null;
    }
  };

  // Method to update panelId in socket
  const updatePanelId = (panelId: string) => {
    setCurrentPanelId(panelId);
    
    if (socket && isConnected) {
      const adminId = getAndSanitize('userId');
      socket.emit('registerPanel', {
        adminId,
        panelId
      });
    } else {
    }
  };

  // Grant access to a user
  const grantUserAccess = (userId: string, username: string) => {
    if (!socket || !isConnected) return;
    
    socket.emit('grantAccess', {
      userId,
      username,
      adminId: getAndSanitize('userId'),
      timestamp: Date.now()
    });
  };

  // Emit an event to the server
  const emit = (event: string, data?: any) => {
    if (socket && isConnected) {
      const enrichedData = {
        ...(data || {}),
        clientId: clientId.current,
        adminId: getAndSanitize('userId'),
        panelId: currentPanelId,
        timestamp: Date.now()
      };
      
      socket.emit(event, enrichedData);
    } else {
      pendingMessages.current.push({ event, data });
    }
  };

  // Fetch active panel ID
  const fetchActivePanelId = async (socketClient: Socket, token: string) => {
    try {
      const userId = extractUserId(token);
      if (!userId) {
        throw new Error('Could not extract user ID from token');
      }
      
      
      // Use the store instead of direct fetch
      const user = await useUserStore.getState().fetchUser(userId);
      
      if (!user) {
        throw new Error('Failed to fetch user data');
      }
      
      const activePanel = user.panels?.find((p: any) => p.isActive);
      
      if (activePanel) {
        setCurrentPanelId(activePanel.id);
        socketClient.emit('registerPanel', {
          adminId: userId,
          panelId: activePanel.id
        });
      } else {
        console.warn('[Socket] No active panel found for user');
      }
    } catch (error) {
      console.error('[Socket] Error fetching active panel:', error);
    }
  };

  // 🚀 Métodos para manejar eventos de tickets
  const onTicketCreated = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('ticket_created', callback);
    }
  };

  const onTicketStatusChanged = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('ticket_status_changed', callback);
    }
  };

  const onNewTicketMessage = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('new_ticket_message', callback);
    }
  };

  const offTicketCreated = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('ticket_created', callback);
    }
  };

  const offTicketStatusChanged = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('ticket_status_changed', callback);
    }
  };

  const offNewTicketMessage = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('new_ticket_message', callback);
    }
  };

  // 🔔 Métodos para manejar eventos de notificaciones
  const onUserInfoNotification = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('userInfoNotification', callback);
    }
  };

  const offUserInfoNotification = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('userInfoNotification', callback);
    }
  };

  const onUserInfoNotificationResolved = (callback: (data: any) => void) => {
    if (socket) {
      socket.on('userInfoNotificationResolved', callback);
    }
  };

  const offUserInfoNotificationResolved = (callback: (data: any) => void) => {
    if (socket) {
      socket.off('userInfoNotificationResolved', callback);
    }
  };

  return (
    <SocketContext.Provider value={{
      socket,
      isConnected,
      connectionStatus,
      lastPing,
      emit,
      lastReconnectTimestamp,
      health,
      updatePanelId,
      grantUserAccess,
      onTicketCreated,
      onTicketStatusChanged,
      onNewTicketMessage,
      offTicketCreated,
      offTicketStatusChanged,
      offNewTicketMessage,
      onUserInfoNotification,
      offUserInfoNotification,
      onUserInfoNotificationResolved,
      offUserInfoNotificationResolved
    }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};